import { Icons, Tooltip, Typography } from '@healthinal/ui';
import {
  getAppointmentDate,
  getAppointmentEndDateTime,
  getAppointmentStartDateTime,
} from '../../../../api/created/appointment.ts';
import {
  AppointmentDto,
  CreateXrayTestDtoFinding,
  LabOrderDto,
  LabOrderDtoInterpretation,
  LabOrderDtoType,
  LabOrderResultDtoType,
  TracingContactDetailsDto,
  TracingContactDetailsDtoAssessmentStatus,
  TracingContactSummaryDto,
} from '../../../../api/generated/model';
import { TruncateText } from '../../../../components/TruncateText.tsx';
import { theme } from '../../../../theme.ts';
import { getLatestAppointment } from '../../../../utils/appointment.ts';
import { getTracingContactAssessmentStatusName } from '../../../../utils/contactTracing.ts';
import { formatDate, isAfterNow } from '../../../../utils/date.ts';
import { getLatestLabOrder, getXrayInterpretationName } from '../../../../utils/labOrder.ts';

type Props = {
  contact: TracingContactSummaryDto;
  showPatientColumn: boolean;
};

type TbiStatusDates = {
  referralDate?: string | null;
  therapyStartDate?: string | null;
  therapyEndDate?: string | null;
};

type TracingContactSummaryWithTbiStatus = TracingContactSummaryDto & TbiStatusDates;

export function TracingContactsTableRow({ contact, showPatientColumn }: Props) {
  return (
    <>
      <td>
        <TruncateText>{contact.tracingContactDetails.lastName}</TruncateText>
      </td>
      <td>
        <TruncateText>{contact.tracingContactDetails.firstName}</TruncateText>
      </td>
      <td>{formatDate(contact.tracingContactDetails.birthDate)}</td>
      {showPatientColumn && (
        <td>
          <TruncateText>{contact.tracingContactDetails.contactPatientCaseNumber}</TruncateText>
        </td>
      )}
      <td>{getIncubationPeriodEnd(contact.tracingContactDetails)}</td>
      <td>{contact.hasTuberculosisHistory && 'Ja'}</td>
      <td>{getLatestAppointmentDate(contact.appointments)}</td>
      <td>{getLatestLabOrderDate(contact.labOrders)}</td>
      <td>{getLatestLabOrderInterpretation(contact.labOrders, LabOrderDtoType.TUBERCULIN)}</td>
      <td>{getLatestLabOrderInterpretation(contact.labOrders, LabOrderDtoType.BLOOD_QUANTIFERON)}</td>
      <td>{getLatestLabOrderInterpretation(contact.labOrders, LabOrderDtoType.BLOOD_TSPOT)}</td>
      <td>{getLatestXrayFinding(contact.labOrders)}</td>
      <td>{getLatestLabOrderInterpretation(contact.labOrders, LabOrderDtoType.SPUTUM)}</td>
      <td>
        <TbiStatusCell contact={contact} />
      </td>
      <td>
        {getCurrentStatus(contact.appointments, contact.labOrders, contact.tracingContactDetails.assessmentStatus)}
      </td>
    </>
  );
}

function TbiStatusCell({ contact }: { contact: TracingContactSummaryDto }) {
  return getTbiDisplay(getTbiStatusDates(contact));
}

function getTbiDisplay(tbiStatusDates: TbiStatusDates | undefined | null) {
  const { referralDate, therapyStartDate, therapyEndDate } = tbiStatusDates ?? {};

  if (!referralDate) return undefined;

  if (therapyStartDate && therapyEndDate) {
    return (
      <Tooltip title={getTbiStatusTooltip(referralDate, therapyStartDate, therapyEndDate)}>
        <Typography color="success">beendet</Typography>
      </Tooltip>
    );
  }

  if (therapyStartDate) {
    return (
      <Tooltip title={getTbiStatusTooltip(referralDate, therapyStartDate, therapyEndDate)}>
        <Typography color="warning">Gestartet</Typography>
      </Tooltip>
    );
  }

  return (
    <Tooltip title={getTbiStatusTooltip(referralDate, therapyStartDate, therapyEndDate)}>
      <Typography textColor={theme.vars.palette.neutral.plainDisabledColor}>überwiesen</Typography>
    </Tooltip>
  );
}

function getTbiStatusDates(contact: TracingContactSummaryDto): TbiStatusDates {
  return contact as TracingContactSummaryWithTbiStatus;
}

function getTbiStatusTooltip(referralDate: string, therapyStartDate?: string | null, therapyEndDate?: string | null) {
  return [
    `Überweisungsdatum: ${formatDate(referralDate)}`,
    therapyStartDate && `Startdatum: ${formatDate(therapyStartDate)}`,
    therapyEndDate && `Enddatum: ${formatDate(therapyEndDate)}`,
  ]
    .filter(Boolean)
    .join('\n');
}

function getCurrentStatus(
  appointments: AppointmentDto[],
  labOrders: LabOrderDto[],
  assessmentStatus: TracingContactDetailsDtoAssessmentStatus | undefined,
) {
  if (!assessmentStatus) return undefined;
  switch (assessmentStatus) {
    case TracingContactDetailsDtoAssessmentStatus.PENDING:
      return (
        <Tooltip title={getTracingContactAssessmentStatusName(assessmentStatus)}>
          <Icons.ErrorOutline size={'sm'} color={'warning'} />
        </Tooltip>
      );
    case TracingContactDetailsDtoAssessmentStatus.NOT_NEEDED:
      return (
        <Tooltip title={getTracingContactAssessmentStatusName(assessmentStatus)}>
          <Icons.RemoveCircleOutline size={'sm'} color={'success'} />
        </Tooltip>
      );
    case TracingContactDetailsDtoAssessmentStatus.WAITING_PERIOD:
      return (
        <Tooltip title={getTracingContactAssessmentStatusName(assessmentStatus)}>
          <Icons.ChangeCircleOutlined size={'sm'} />
        </Tooltip>
      );
    case TracingContactDetailsDtoAssessmentStatus.RESULT_POSITIVE:
    case TracingContactDetailsDtoAssessmentStatus.RESULT_NEGATIVE:
    case TracingContactDetailsDtoAssessmentStatus.RESULT_INDETERMINATE:
      return (
        <Tooltip title={`Resultat vorhanden: ${getTracingContactAssessmentStatusName(assessmentStatus)}`}>
          <Icons.CheckCircleOutline color={'success'} size={'sm'} />
        </Tooltip>
      );
    case TracingContactDetailsDtoAssessmentStatus.APPOINTMENT_VISIT: {
      const latestAppointment = getLatestAppointment(appointments);
      if (!latestAppointment) {
        return (
          <Tooltip title={'Kein Termin vorhanden'}>
            <Icons.EventAvailable size={'sm'} color={'danger'} />
          </Tooltip>
        );
      }

      if (isAfterNow(getAppointmentStartDateTime(latestAppointment))) {
        return (
          <Tooltip title={'Termin vereinbart'}>
            <Icons.EventAvailable size={'sm'} />
          </Tooltip>
        );
      }

      if (latestAppointment.isAttended === false) {
        return (
          <Tooltip title={'Termin nicht durchgeführt'}>
            <Icons.EventAvailable size={'sm'} color={'danger'} />
          </Tooltip>
        );
      }
      const labOrderForAppointment = labOrders.find((order) => order.appointmentId === latestAppointment.id);

      if (!labOrderForAppointment) {
        return (
          <Tooltip title={'Noch kein Testresultat für diesen Termin vorhanden'}>
            <Icons.EventAvailable size={'sm'} color={'warning'} />
          </Tooltip>
        );
      }

      return (
        <Tooltip title={'Testresultat vorhanden'}>
          <Icons.BloodtypeOutlined size={'sm'} />
        </Tooltip>
      );
    }
  }
}

function getIncubationPeriodEnd(contactDetails: TracingContactDetailsDto) {
  if (!contactDetails.incubationPeriodEnd) return undefined;

  if (isAfterNow(contactDetails.incubationPeriodEnd)) {
    if (contactDetails.assessmentStatus === TracingContactDetailsDtoAssessmentStatus.WAITING_PERIOD) {
      return (
        <Tooltip title={'Inkubationszeit aktivieren'}>
          <Typography color={'success'} endDecorator={<Icons.ChangeCircleOutlined size={'sm'} />}>
            {formatDate(contactDetails.incubationPeriodEnd)}
          </Typography>
        </Tooltip>
      );
    }
    return formatDate(contactDetails.incubationPeriodEnd);
  }
  if (contactDetails.assessmentStatus === TracingContactDetailsDtoAssessmentStatus.WAITING_PERIOD) {
    return (
      <Tooltip title={'Enddatum der Wartefrist wurde überschritten'}>
        <Typography color={'warning'} endDecorator={<Icons.ErrorOutline size={'sm'} />}>
          {formatDate(contactDetails.incubationPeriodEnd)}
        </Typography>
      </Tooltip>
    );
  }
  return (
    <Typography textColor={theme.vars.palette.neutral.plainDisabledColor}>
      {formatDate(contactDetails.incubationPeriodEnd)}
    </Typography>
  );
}

function getLatestXrayFinding(labOrders: LabOrderDto[]) {
  const latestXrayLabOrder = getLatestLabOrder(labOrders.filter((order) => order.type === LabOrderDtoType.XRAY));
  const xrayFindingResult = latestXrayLabOrder?.results.find(
    (result) => result.type === LabOrderResultDtoType.XRAY_FINDING,
  );
  const xrayFinding = getXrayFindingFromString(xrayFindingResult?.result);

  if (!xrayFinding) return undefined;

  return (
    <Tooltip title={`Röntgenbefund: ${getXrayInterpretationName(xrayFinding)}`}>
      <Typography>{getXrayFindingStringShort(xrayFinding)}</Typography>
    </Tooltip>
  );
}

function getXrayFindingStringShort(finding: CreateXrayTestDtoFinding) {
  switch (finding) {
    case CreateXrayTestDtoFinding.NORMAL:
      return 'N';
    case CreateXrayTestDtoFinding.ABNORMAL_TB_SUSPICION:
      return 'A+';
    case CreateXrayTestDtoFinding.ABNORMAL_TB_UNLIKELY:
      return 'A-';
  }
}

function getXrayFindingFromString(finding?: string) {
  return Object.values(CreateXrayTestDtoFinding).find((value) => value === finding);
}

function getLatestLabOrderInterpretation(labOrders: LabOrderDto[], type: LabOrderDtoType) {
  const latestLabOrder = getLatestLabOrder(labOrders.filter((order) => order.type === type));

  if (!latestLabOrder) return undefined;

  return getLabOrderInterpretationStringShort(latestLabOrder.interpretation);
}

function getLabOrderInterpretationStringShort(interpretation: LabOrderDtoInterpretation) {
  switch (interpretation) {
    case LabOrderDtoInterpretation.POSITIVE:
      return 'P';
    case LabOrderDtoInterpretation.NEGATIVE:
      return 'N';
    default:
      return 'U';
  }
}

function getLatestLabOrderDate(labOrders: LabOrderDto[]) {
  const latestLabOrder = getLatestLabOrder(labOrders);

  if (!latestLabOrder) return undefined;

  if (
    latestLabOrder.interpretation !== LabOrderDtoInterpretation.PENDING &&
    latestLabOrder.interpretation !== LabOrderDtoInterpretation.NONE
  ) {
    return formatDate(latestLabOrder.samplingDate);
  }

  if (latestLabOrder.results.find((result) => result.type === LabOrderResultDtoType.XRAY_FINDING)) {
    return formatDate(latestLabOrder.samplingDate);
  }

  return (
    <Tooltip title={'Noch kein Testresultat vorhanden'}>
      <Typography color={'warning'} endDecorator={<Icons.ErrorOutline color={'warning'} size={'sm'} />}>
        {formatDate(latestLabOrder.samplingDate)}
      </Typography>
    </Tooltip>
  );
}

function getLatestAppointmentDate(appointments: AppointmentDto[]) {
  const latestAppointment = getLatestAppointment(appointments);

  if (!latestAppointment) return undefined;

  if (isAfterNow(getAppointmentEndDateTime(latestAppointment))) {
    return formatDate(getAppointmentDate(latestAppointment));
  }

  if (latestAppointment.isAttended) {
    return (
      <Typography textColor={'success'}>{formatDate(getAppointmentDate(latestAppointment))}</Typography>
    );
  }

  if (latestAppointment.isAttended === false) {
    return (
      <Tooltip title={'Nicht durchgeführt'}>
        <Typography color={'danger'} endDecorator={<Icons.ErrorOutline size={'sm'} />}>
          {formatDate(getAppointmentDate(latestAppointment))}
        </Typography>
      </Tooltip>
    );
  }

  return (
    <Tooltip title={'Datum überschritten aber noch kein Testresultat erfasst'}>
      <Typography color={'warning'} endDecorator={<Icons.HelpOutline size={'sm'} />}>
        {formatDate(getAppointmentDate(latestAppointment))}
      </Typography>
    </Tooltip>
  );
}
